import isEmpty from 'lodash-es/isEmpty'
import some from 'lodash-es/some'

import { MapObject } from '../models'
import { isRuDomain } from './domain'
import { formatTime, parseDate } from './formatMethods'

export * from './apiUtils'
export * from './camelizeJsonUtils'
export * from './errorUtils'
export * from './validators'
export * from './nameofFactory'
export * from './historyUtils'
export * from './mediaPreview'
export * from './parse/reorder'

function normalizeLogin(login: string): string {
    login = login || ''
    login = login.toLowerCase().trim()

    if (login.indexOf('|')) {
        login = login.substr(login.lastIndexOf('|') + 1)
    }
    return login
}

export const compareLogins = (firstLogin: string, secondLogin: string): boolean => {
    firstLogin = normalizeLogin(firstLogin)
    secondLogin = normalizeLogin(secondLogin)
    return firstLogin === secondLogin
}

export const endsWith = (text: string, suffix: string, ignoreCase: boolean = true): boolean => {
    if (ignoreCase) {
        text = text.toLowerCase()
        suffix = suffix.toLowerCase()
    }
    return text.indexOf(suffix, text.length - suffix.length) !== -1
}

export const containsIgnoreCase = (text: string, value: string): boolean => {
    if (!text) text = ''
    if (!value) value = ''
    return text.toLowerCase().indexOf(value.toLowerCase()) !== -1
}

export const convertToString = (value: any, defaultValue: string = ''): string => {
    if (!!value) {
        return typeof value === 'string' ? value : value.toString()
    }
    return defaultValue
}

interface EnumType {
    [key: number]: any

    [key: string]: any
}

export const convertToEnum = <T>(type: EnumType, value: string | number, defaultValue: T): T => {
    let enumKeys = Object.keys(type)
    defaultValue = defaultValue || type[enumKeys[0]]

    let enumValue: any = defaultValue
    enumKeys.forEach((enumKey) => {
        if (value === type[enumKey]) {
            enumValue = value
            return
        }
    })

    return enumValue
}

export const notEmptyString = (value: string): boolean => {
    if (!!value && typeof value !== 'string') {
        value = (value as any).toString()
    }
    return !isEmpty(value)
}

export const emptyString = (value: string): boolean => {
    if (!!value && typeof value !== 'string') {
        value = (value as any).toString()
    }
    return isEmpty(value)
}

export const someTrue = (...values: boolean[]): boolean => {
    return some(values, Boolean)
}

export const someFalse = (...values: boolean[]): boolean => {
    return some(values, (b) => !b)
}

export const makeDelay = (fn: () => void, ms: number = 2000) => () => setTimeout(fn, ms)

export const makeSorter = (key: string, orderAsc: boolean) => (a, b) => {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
        return 0
    }

    const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key]
    const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key]

    let comparison = 0

    if (varA > varB) {
        comparison = 1
    } else if (varA < varB) {
        comparison = -1
    }

    if (!orderAsc) {
        comparison *= -1
    }

    return comparison
}

export const paginate = (items: any[], pageNumber, pageSize): any[] => {
    const pageIndexStart = (pageNumber - 1) * pageSize
    const pageIndexEnd = Math.min(pageIndexStart + pageSize, items.length)
    return items.slice(pageIndexStart, pageIndexEnd)
}

export const inArray = (value: any, values: any[]): boolean => {
    values = values || []
    return values.some((valueToCompare) => valueToCompare === value)
}

export const intersectionOfArrays = (arrA: any[], arrB: any[]): any[] => {
    return arrA.filter((x) => inArray(x, arrB))
}
export const arrayMove = <T>(array: T[], from: number, to: number): T[] => {
    array = array.slice()
    array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0])
    return array
}

export const isNaNOrDefault = (value: number, defaultValue: any = ''): any => {
    return isNaN(value) ? defaultValue : value
}

export const yepNope = <T>(truthful: boolean, yep: T, nope: T) => {
    return truthful ? yep : nope
}

export const isNullOrUndefined = (value): boolean => {
    return value === null || value === undefined
}

export const isNullOrEmptyString = (value: string): boolean => {
    return value === null || value === undefined || value === ''
}

export const arrayToObjectMap = <T>(array: T[], prop: keyof T): MapObject<T> => {
    return array.reduce((target, item) => {
        target[item[prop]] = item
        return target
    }, {} as any)
}

export const parseDateProps = (obj: any, ...props: string[]): any => {
    return Object.keys(obj).reduce((target, key) => {
        return props.indexOf(key) === -1 ? target : { ...target, [key]: parseDate(obj[key]) }
    }, obj)
}

export const parseDatePropsTyped = <T>(obj: any, ...props: Array<keyof T>): any => {
    return Object.keys(obj).reduce((target, key) => {
        return !props.includes(key as any) ? target : { ...target, [key]: parseDate(obj[key]) }
    }, obj)
}

export const parseTimeProps = (obj: any, ...props: string[]): any => {
    return Object.keys(obj).reduce((target, key) => {
        return props.indexOf(key) === -1 ? target : { ...target, [key]: formatTime(obj[key]) }
    }, obj)
}

export const isObject = (obj: any): boolean => {
    if (obj === null || obj === undefined) return false
    if (typeof obj === 'string') return false
    if (typeof obj === 'function') return false
    return obj === Object(obj)
}

export const getDescriptionText = (isShowDesc: boolean) => (text: string) => (isShowDesc ? text : '')

export const smoothScrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
}

export { isRuDomain }

export const getFavicarDomain = (): string => (isRuDomain() ? 'ru' : 'com')

export { getMissingPermissions, checkUserPermissions } from './permissions'
